import { CategoryTag, ProposalItemsList } from "@/components/proposal-items";
import { ProposalOutline, type OutlineSection } from "@/components/proposal-outline";
import { ProposalAvailability, hasAvailability } from "@/components/proposal-availability";
import { ProposalTimeline } from "@/components/proposal-timeline";
import { Reveal, SectionTitle } from "@/components/proposal-section";
import { RichText } from "@/components/rich-text";
import profilePhoto from "@/assets/profile-photo.webp.asset.json";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { StripeEmbeddedCheckoutForm } from "@/components/stripe-embedded-checkout";
import {
  confirmProposalPayment,
  declineProposal,
  getProposalPreview,
  getProposalShareMeta,
  getPublicProposal,
  signProposal,
  requestSignatureCode,
  startProposalPayment,
  
} from "@/lib/proposals.functions";
import {
  cycleLabelIn,
  formatDateIn,
  formatDateTimeIn,
  formatMoneyIn,
  normalizeLocale,
  proposalStrings,
  PROPOSAL_LOCALES,
  type ProposalLocale,
} from "@/lib/proposals-i18n";
import {
  billingProblems,
  formatBillingAddress,

  computeTotalCents,
  EMPTY_BILLING,
  lineTotal,
  proposalVatMode,
  taxLinesFor,
  timelineSpanDays,
  type BillingDetails,
  type PublicProposal,
} from "@/lib/proposals-types";
import { computeTaxTotals, determineVatMode, vatNote, vatRateLabel } from "@/lib/tax";


export const Route = createFileRoute("/p/$token")({
  // data-only: the loader runs on the server so crawlers get real head tags,
  // while the interactive body still renders client-side.
  ssr: "data-only",
  component: ProposalPage,
  loader: async ({ params }) => {
    try {
      return { share: await getProposalShareMeta({ data: { token: params.token } }) };
    } catch {
      return { share: null };
    }
  },
  head: ({ loaderData }) => {
    const share = loaderData?.share ?? null;
    const title = share?.title ?? "Your proposal";
    const description =
      share?.description ??
      "A tailored scope, timeline and next steps from the Vibing Company. Review the details and sign online.";
    return {
      meta: [
        { title },
        { name: "robots", content: "noindex, nofollow" },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(share?.imageUrl
          ? [
              { property: "og:image", content: share.imageUrl },
              { property: "og:image:width", content: "1200" },
              { property: "og:image:height", content: "630" },
              { name: "twitter:image", content: share.imageUrl },
            ]
          : []),
      ],
    };
  },
});


function ProposalPage() {
  const { token } = Route.useParams();
  const isPreview =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("preview") === "1";

  const fetchProposal = useServerFn(getPublicProposal);
  const fetchPreview = useServerFn(getProposalPreview);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["public-proposal", token, isPreview],
    queryFn: () =>
      isPreview ? fetchPreview({ data: { token } }) : fetchProposal({ data: { token } }),
    retry: false,
  });

  // The link-preview card is rendered in the admin studio whenever a proposal
  // is saved or changed — nothing is generated here on link opening.




  const browserLocale = normalizeLocale(
    typeof navigator !== "undefined" ? navigator.language : "en",
  );
  const tFallback = proposalStrings(browserLocale);

  if (isLoading)
    return (
      <Shell>
        <p className="text-muted-foreground">{tFallback.loading}</p>
      </Shell>
    );
  if (!data) {
    return (
      <Shell>
        <h1 className="fluid-h2 font-display tracking-tight">{tFallback.unavailableTitle}</h1>
        <p className="mt-4 text-muted-foreground">
          {isPreview && error ? tFallback.previewNeedsAdmin : tFallback.unavailableBody}
        </p>
      </Shell>
    );
  }

  return (
    <ProposalView
      proposal={data}
      token={token}
      preview={isPreview}
      onRefresh={() => refetch()}
    />
  );
}

function Shell({ children, wide = false }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="min-h-dvh bg-background">
      <main
        id="main"
        tabIndex={-1}
        className={`mx-auto ${wide ? "max-w-6xl xl:max-w-7xl" : "max-w-3xl"} px-6 pt-[clamp(5rem,10vh,7rem)] pb-32 lg:pb-24`}
      >
        {children}
      </main>
    </div>
  );
}

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function ProposalView({
  proposal,
  token,
  preview = false,
  onRefresh,
}: {
  proposal: PublicProposal;
  token: string;
  preview?: boolean;
  onRefresh: () => void;
}) {
  const readOnly = preview;
  const signed = !!proposal.signature;

  const [locale, setLocale] = useState<ProposalLocale>(() => normalizeLocale(proposal.language));
  useEffect(() => {
    setLocale(normalizeLocale(proposal.language));
  }, [proposal.language]);
  const t = proposalStrings(locale);
  const money = (cents: number | null | undefined) =>
    formatMoneyIn(cents, proposal.currency, locale);

  const [selected, setSelected] = useState<string[]>(
    () =>
      proposal.signature?.selected_addon_ids ??
      proposal.items.filter((i) => i.is_addon && i.default_selected).map((i) => i.id),
  );
  const [signerName, setSignerName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paid, setPaid] = useState(proposal.status === "paid");
  const [billing, setBilling] = useState<BillingDetails>(() => ({
    ...EMPTY_BILLING,
    ...(proposal.billing ?? {}),
    billing_name: proposal.billing?.billing_name || proposal.company || proposal.client_name || "",
    billing_email: proposal.billing?.billing_email || proposal.client_email || "",
  }));
  const setField = (key: keyof BillingDetails, value: string) =>
    setBilling((prev) => ({ ...prev, [key]: value }));
  const billingMissing = billingProblems(billing).length > 0;

  const core = proposal.items.filter((i) => !i.is_addon);
  const addons = proposal.items.filter((i) => i.is_addon);
  const total = useMemo(
    () => (signed ? proposal.signature!.total_cents : computeTotalCents(proposal.items, selected)),
    [proposal, selected, signed],
  );
  // VAT preview follows the address the client is typing; after signing it is
  // fixed to the mode stored on the proposal.
  const taxTotals = useMemo(() => {
    const mode = signed
      ? proposalVatMode(proposal.vat_mode)
      : determineVatMode({ country: billing.billing_country, vatId: billing.billing_vat_id });
    const addonIds = signed ? proposal.signature!.selected_addon_ids : selected;
    return computeTaxTotals(taxLinesFor(proposal.items, addonIds), mode);
  }, [proposal, selected, signed, billing.billing_country, billing.billing_vat_id]);
  const taxNote = vatNote(taxTotals.mode, locale);
  const baseTotal = useMemo(() => computeTotalCents(core, []), [core]);
  const selectedAddonCount = addons.filter((a) => selected.includes(a.id)).length;
  const spanDays = timelineSpanDays(proposal.timeline_points);


  const sign = useServerFn(signProposal);
  const requestCode = useServerFn(requestSignatureCode);
  const [code, setCode] = useState("");
  const [codeSentTo, setCodeSentTo] = useState<string | null>(null);
  const codeMutation = useMutation({
    mutationFn: () => requestCode({ data: { token, email: billing.billing_email } }),
    onSuccess: (res) => {
      if ("error" in res) setError(res.error);
      else {
        setError(null);
        setCodeSentTo(billing.billing_email);
      }
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Could not send the code"),
  });
  const decline = useServerFn(declineProposal);
  const startPayment = useServerFn(startProposalPayment);
  const confirmPayment = useServerFn(confirmProposalPayment);

  // Reveal the side rails once the reader starts scrolling.
  const [railsVisible, setRailsVisible] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onScroll = () => setRailsVisible(window.scrollY > 120);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const railClass = (from: string) =>
    `transition-all duration-500 ease-out motion-reduce:transition-none ${
      railsVisible ? "opacity-100 translate-x-0 translate-y-0" : `opacity-0 pointer-events-none ${from}`
    }`;



  // Returning from Stripe embedded checkout.
  useEffect(() => {
    if (typeof window === "undefined" || readOnly) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId || paid) return;

    void confirmPayment({ data: { token, sessionId } }).then((res) => {
      if (res.paid) {
        setPaid(true);
        setClientSecret(null);
        window.history.replaceState({}, "", window.location.pathname);
        onRefresh();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signMutation = useMutation({
    mutationFn: () => sign({ data: { token, signerName, addonIds: selected, agreed, billing, code } }),
    onSuccess: async (res) => {
      if ("error" in res) {
        setError(res.error);
        return;
      }
      setError(null);
      onRefresh();
      const pay = await startPayment({ data: { token } });
      if ("error" in pay) setError(pay.error);
      else setClientSecret(pay.clientSecret);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Signing failed"),
  });

  const payMutation = useMutation({
    mutationFn: () => startPayment({ data: { token } }),
    onSuccess: (res) => {
      if ("error" in res) setError(res.error);
      else setClientSecret(res.clientSecret);
    },
  });

  const declineMutation = useMutation({
    mutationFn: () => decline({ data: { token } }),
    onSuccess: () => onRefresh(),
  });

  if (proposal.expired && !signed && !readOnly) {
    return (
      <Shell>
        <h1 className="fluid-h2 font-display tracking-tight">{t.expiredTitle}</h1>
        <p className="mt-4 text-muted-foreground">
          {t.expiredBody(formatDateIn(proposal.valid_until, locale))}
        </p>
      </Shell>
    );
  }

  const contactName = proposal.client_name?.trim() || "";
  const namePlaceholder = contactName || t.namePlaceholder;
  const issuerName =
    proposal.company_profile?.owner_name ??
    proposal.company_profile?.legal_name ??
    "Jonathan Osburg";

  const docRef = useRef<HTMLDivElement | null>(null);
  const sections = useMemo<OutlineSection[]>(() => {
    const list: OutlineSection[] = [];
    const s = t.sections;
    if (proposal.intro)
      list.push({ id: "overview", label: s.overview, blurb: s.overviewBlurb });
    list.push({
      id: "engagement",
      label: s.engagement,
      blurb: addons.length > 0 ? s.engagementBlurbAddons : s.engagementBlurb,
    });
    if (proposal.scope) list.push({ id: "scope", label: s.scope, blurb: s.scopeBlurb });
    if (proposal.deliverables)
      list.push({ id: "deliverables", label: s.deliverables, blurb: s.deliverablesBlurb });
    if (hasAvailability(proposal))
      list.push({
        id: "availability",
        label: t.availabilityHeading,
        blurb: t.startDisclaimer,
      });
    if (proposal.timeline_points.length > 0 || proposal.timeline)
      list.push({ id: "timeline", label: s.timeline, blurb: s.timelineBlurb });
    if (proposal.assumptions)
      list.push({ id: "assumptions", label: s.assumptions, blurb: s.assumptionsBlurb });
    if (proposal.contract_body)
      list.push({ id: "contract", label: s.contract, blurb: s.contractBlurb });
    list.push({
      id: "sign",
      label: signed ? s.signature : s.sign,
      blurb: signed ? s.signatureBlurb : s.signBlurb,
    });
    return list;
  }, [proposal, addons.length, signed, t]);

  return (
    <Shell wide>
      {readOnly ? (
        <div
          role="status"
          className="mb-8 rounded-2xl border border-amber-500/30 bg-amber-500/5 px-5 py-4"
        >
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-amber-600">
            {t.adminPreview(proposal.status)}
          </p>
          <p className="mt-2 text-sm text-foreground/80">
            {proposal.status === "draft" ? t.adminPreviewDraft : t.adminPreviewReadOnly}
          </p>
        </div>
      ) : null}

      <div className="grid lg:grid-cols-[minmax(0,1fr)_20rem] lg:gap-14 xl:grid-cols-[13rem_minmax(0,1fr)_20rem]">
        <header className="max-w-3xl xl:col-start-2">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
            the Vibing Company® · {t.proposalLabel} {proposal.number}
          </p>
          <div
            role="group"
            aria-label={t.languageLabel}
            className="flex items-center gap-1 rounded-full border border-border p-1"
          >
            {PROPOSAL_LOCALES.map((code) => (
              <button
                key={code}
                type="button"
                aria-pressed={locale === code}
                onClick={() => setLocale(code)}
                className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  locale === code
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {code}
              </button>
            ))}
          </div>
        </div>
        <h1 className="mt-3 font-display text-[clamp(2rem,4.4vw,3.5rem)] leading-[1.02] tracking-[-0.035em] text-foreground text-balance">
          {proposal.title}
        </h1>
        <p className="mt-4 text-sm text-muted-foreground">
          {[proposal.client_name, proposal.company].filter(Boolean).join(" · ") ||
            t.preparedForYou}
          {proposal.valid_until ? t.validUntilInline(formatDateIn(proposal.valid_until, locale)) : ""}
        </p>
        </header>
      </div>

      <div className="mt-12 grid gap-12 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:gap-14 xl:grid-cols-[13rem_minmax(0,1fr)_20rem]">
        {/* Left: reading outline */}
        <div className={`hidden lg:block lg:sticky lg:top-24 ${railClass("-translate-x-4")}`} aria-hidden={!railsVisible}>
          <ProposalOutline sections={sections} targetRef={docRef} heading={t.outlineHeading} />
        </div>


        {/* Middle: the document */}
        <div ref={docRef} className="min-w-0">
          {proposal.intro ? (
            <figure id="overview" className="scroll-mt-28">
              <blockquote>
                <RichText
                  text={proposal.intro}
                  className="text-[1.0625rem] leading-relaxed text-foreground/90 text-pretty [&_p]:italic"
                />
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-4 text-left">
                <img
                  src={profilePhoto.url}
                  alt={issuerName}
                  loading="lazy"
                  className="h-14 w-14 shrink-0 rounded-full object-cover outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/10"
                />
                <span className="text-[12px] leading-snug">
                  <span className="block font-medium text-foreground">{issuerName}</span>
                  <span className="block text-muted-foreground">Founder of the Vibing Co.</span>
                </span>
              </figcaption>
            </figure>


          ) : null}


          <Reveal>
          <section
            id="engagement"
            aria-labelledby="receipt-heading"
            className={`scroll-mt-28 ${proposal.intro ? "mt-16" : ""}`}
          >
            <SectionTitle id="receipt-heading">{t.sections.engagement}</SectionTitle>
            <ProposalItemsList items={core} currency={proposal.currency} locale={locale} />

            {addons.length > 0 ? (
              <fieldset className="mt-8" disabled={signed || readOnly}>
                <legend className="font-display text-lg leading-tight tracking-[-0.01em] text-foreground">
                  {t.addonsLegend}
                </legend>
                <p className="mt-2 text-xs text-muted-foreground">
                  {t.addonsHint}
                </p>
                <ul className="mt-3 space-y-2">
                  {addons.map((item) => {
                    const on = selected.includes(item.id);
                    return (
                      <li key={item.id}>
                        <label
                          className={`flex cursor-pointer items-start justify-between gap-6 rounded-2xl border px-5 py-4 transition-[background-color,border-color,scale] duration-200 focus-within:ring-2 focus-within:ring-ring ${
                            on
                              ? "border-foreground/40 bg-card"
                              : "border-border bg-transparent hover:border-foreground/25"
                          } ${signed || readOnly ? "cursor-default opacity-70" : "active:scale-[0.99]"}`}
                        >
                          <span className="flex min-w-0 items-start gap-3">
                            <input
                              type="checkbox"
                              className="mt-1 size-4 shrink-0 accent-current"
                              checked={on}
                              disabled={signed || readOnly}
                              onChange={(e) =>
                                setSelected((prev) =>
                                  e.target.checked
                                    ? [...prev, item.id]
                                    : prev.filter((id) => id !== item.id),
                                )
                              }
                            />
                            <span className="min-w-0">
                              <span className="block text-sm font-medium text-foreground">
                                {item.title}
                              </span>
                              {item.description ? (
                                <span className="mt-1 block text-sm text-muted-foreground">
                                  {item.description}
                                </span>
                              ) : null}
                              <CategoryTag category={item.category} locale={locale} />
                            </span>
                          </span>
                          <span className="shrink-0 tabular-nums text-sm text-foreground">
                            +{money(lineTotal(item))}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </fieldset>
            ) : null}
          </section>
          </Reveal>

          {proposal.scope ? (
            <Prose id="scope" heading={t.sections.scope} body={proposal.scope} />
          ) : null}
          {proposal.deliverables ? (
            <Prose id="deliverables" heading={t.sections.deliverables} body={proposal.deliverables} />
          ) : null}

          {hasAvailability(proposal) ? (
            <Reveal>
              <section
                id="availability"
                aria-labelledby="availability-heading"
                className="mt-16 scroll-mt-28"
              >
                <SectionTitle id="availability-heading">{t.availabilityHeading}</SectionTitle>
                <ProposalAvailability data={proposal} locale={locale} className="mt-4" />
              </section>
            </Reveal>
          ) : null}

          {proposal.timeline_points.length > 0 ? (
            <Reveal>
            <section id="timeline" aria-labelledby="timeline-heading" className="mt-16 scroll-mt-28">
              <SectionTitle id="timeline-heading">{t.sections.timeline}</SectionTitle>
              <div className="mt-4 rounded-2xl border border-border bg-card p-5">
                <ProposalTimeline
                  points={proposal.timeline_points}
                  note={proposal.timeline_note}
                  locale={locale}
                />
              </div>
            </section>
            </Reveal>
          ) : proposal.timeline ? (
            <Prose id="timeline" heading={t.sections.timeline} body={proposal.timeline} />
          ) : null}

          {proposal.assumptions ? (
            <Prose id="assumptions" heading={t.sections.assumptions} body={proposal.assumptions} />
          ) : null}

          {proposal.contract_body ? (
            <Reveal>
            <section id="contract" aria-labelledby="contract-heading" className="mt-16 scroll-mt-28">
              <SectionTitle
                id="contract-heading"
                aside={
                  <a
                    href={`/p/${token}/contract`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-foreground/70 underline underline-offset-4 hover:text-foreground"
                  >
                    {t.downloadPdf}
                  </a>
                }
              >
                {t.sections.contract}
              </SectionTitle>
              <div className="mt-4 max-h-96 overflow-y-auto rounded-2xl border border-border bg-card p-5">
                <RichText
                  text={proposal.contract_body}
                  className="text-sm leading-relaxed text-foreground/80"
                />
              </div>
            </section>
            </Reveal>
          ) : null}

          {/* Signature + payment */}
          <Reveal>
          <section id="sign" aria-labelledby="sign-heading" className="mt-16 scroll-mt-28">
            <SectionTitle id="sign-heading">
              {signed ? t.sections.signature : t.signToAccept}
            </SectionTitle>

            {signed ? (
              <div className="mt-4 rounded-2xl border border-border bg-card p-5">
                <p className="font-display text-2xl tracking-tight text-foreground">
                  {proposal.signature!.signer_name}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  {t.signedOn(formatDateTimeIn(proposal.signature!.signed_at, locale))} ·{" "}
                  {t.agreedTotal} {money(taxTotals.grossCents)}
                </p>
                <p className="mt-2 break-all font-mono text-[10px] text-muted-foreground">
                  SHA-256 {proposal.signature!.content_hash}
                </p>

                {proposal.billing ? (
                  <div className="mt-5 grid gap-5 border-t border-border pt-4 sm:grid-cols-2">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                        {t.billTo}
                      </p>
                      <address className="mt-2 text-sm not-italic leading-relaxed text-foreground/85">
                        {formatBillingAddress(proposal.billing).map((line) => (
                          <span key={line} className="block">
                            {line}
                          </span>
                        ))}
                        {proposal.billing.billing_vat_id ? (
                          <span className="block text-muted-foreground">
                            {t.vatIdLabel} {proposal.billing.billing_vat_id}
                          </span>
                        ) : null}
                      </address>
                    </div>
                    <dl className="space-y-1 text-sm">
                      <div className="flex items-baseline justify-between gap-4">
                        <dt className="text-muted-foreground">{t.netTotal}</dt>
                        <dd className="tabular-nums text-foreground">{money(taxTotals.netCents)}</dd>
                      </div>
                      {taxTotals.buckets.map((b) => (
                        <div key={b.rateBp} className="flex items-baseline justify-between gap-4">
                          <dt className="text-muted-foreground">
                            {t.vatLine(vatRateLabel(b.rateBp, locale))}
                          </dt>
                          <dd className="tabular-nums text-foreground">{money(b.vatCents)}</dd>
                        </div>
                      ))}
                      <div className="flex items-baseline justify-between gap-4 font-medium">
                        <dt className="text-foreground">{t.grossTotal}</dt>
                        <dd className="tabular-nums text-foreground">
                          {money(taxTotals.grossCents)}
                        </dd>
                      </div>
                      {taxNote ? (
                        <p className="pt-1 text-xs text-muted-foreground">{taxNote}</p>
                      ) : null}
                    </dl>
                  </div>
                ) : null}

                {proposal.invoice ? (
                  <div className="mt-5 border-t border-border pt-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      {t.invoiceHeading}
                    </p>
                    <p className="mt-2 text-sm text-foreground/85">
                      {t.invoiceNumberLabel} {proposal.invoice.number} ·{" "}
                      {t.invoiceIssued} {formatDateIn(proposal.invoice.issued_at, locale)}
                    </p>
                    <a
                      href={`/p/${token}/contract`}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-block text-xs text-foreground/70 underline underline-offset-4 hover:text-foreground"
                    >
                      {t.invoiceDownload}
                    </a>
                  </div>
                ) : null}
              </div>
            ) : (

              <div className="mt-4 rounded-2xl border border-border bg-card p-5">
                <p className="text-sm text-foreground/85">
                  {t.signingFor(money(taxTotals.grossCents), cycleLabelIn(proposal.billing_cycle, locale))}
                  {selectedAddonCount > 0 ? t.includingAddons(selectedAddonCount) : ""}.
                </p>

                <fieldset className="mt-6" disabled={readOnly}>
                  <legend className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                    {t.billingHeading}
                  </legend>
                  <p className="mt-2 text-xs text-muted-foreground">{t.billingHint}</p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <Field
                      id="billing_name"
                      label={t.billingCompany}
                      value={billing.billing_name}
                      onChange={(v) => setField("billing_name", v)}
                      autoComplete="organization"
                      className="sm:col-span-2"
                    />
                    <Field
                      id="billing_email"
                      label={t.billingEmail}
                      hint={t.billingEmailHint}
                      type="email"
                      value={billing.billing_email}
                      onChange={(v) => setField("billing_email", v)}
                      autoComplete="email"
                      className="sm:col-span-2"
                    />

                    <Field
                      id="billing_street"
                      label={t.billingStreet}
                      value={billing.billing_street}
                      onChange={(v) => setField("billing_street", v)}
                      autoComplete="street-address"
                      className="sm:col-span-2"
                    />
                    <Field
                      id="billing_postcode"
                      label={t.billingPostcode}
                      value={billing.billing_postcode}
                      onChange={(v) => setField("billing_postcode", v)}
                      autoComplete="postal-code"
                    />
                    <Field
                      id="billing_city"
                      label={t.billingCity}
                      value={billing.billing_city}
                      onChange={(v) => setField("billing_city", v)}
                      autoComplete="address-level2"
                    />
                    <Field
                      id="billing_country"
                      label={t.billingCountry}
                      value={billing.billing_country}
                      onChange={(v) => setField("billing_country", v.toUpperCase().slice(0, 2))}
                      autoComplete="country"
                      placeholder="DE"
                    />
                    <Field
                      id="billing_vat_id"
                      label={`${t.billingVatId} (${t.billingOptional})`}
                      value={billing.billing_vat_id ?? ""}
                      onChange={(v) => setField("billing_vat_id", v.toUpperCase())}
                      hint={t.billingVatIdHint}
                    />
                    <Field
                      id="billing_reference"
                      label={`${t.billingReference} (${t.billingOptional})`}
                      value={billing.billing_reference ?? ""}
                      onChange={(v) => setField("billing_reference", v)}
                      hint={t.billingReferenceHint}
                      className="sm:col-span-2"
                    />
                  </div>
                </fieldset>

                <dl className="mt-6 space-y-1 border-t border-border pt-4 text-sm">
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="text-muted-foreground">{t.netTotal}</dt>
                    <dd className="tabular-nums text-foreground">{money(taxTotals.netCents)}</dd>
                  </div>
                  {taxTotals.buckets.map((b) => (
                    <div key={b.rateBp} className="flex items-baseline justify-between gap-4">
                      <dt className="text-muted-foreground">
                        {t.vatLine(vatRateLabel(b.rateBp, locale))}
                      </dt>
                      <dd className="tabular-nums text-foreground">{money(b.vatCents)}</dd>
                    </div>
                  ))}
                  <div className="flex items-baseline justify-between gap-4 pt-1 font-medium">
                    <dt className="text-foreground">{t.grossTotal}</dt>
                    <dd className="tabular-nums text-foreground">{money(taxTotals.grossCents)}</dd>
                  </div>
                </dl>
                {taxNote ? <p className="mt-2 text-xs text-muted-foreground">{taxNote}</p> : null}

                <label htmlFor="signer" className="mt-6 block text-sm text-foreground">
                  {t.yourLegalName}
                </label>
                <input
                  id="signer"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  autoComplete="name"
                  disabled={readOnly}
                  className="mt-2 w-full rounded-xl border border-border bg-background px-4 py-3 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder={namePlaceholder}
                />
                <label className="mt-4 flex items-start gap-3 text-sm text-foreground/85">
                  <input
                    type="checkbox"
                    checked={agreed}
                    disabled={readOnly}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-1 size-4 accent-current"
                  />
                  <span>{t.consent(proposal.company || contactName || t.theClient)}</span>
                </label>
                <p className="mt-3 text-xs text-muted-foreground">
                  {t.signingNotice}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">{t.electronicFormClause}</p>
                {billingMissing ? (
                  <p className="mt-3 text-xs text-muted-foreground">{t.billingIncomplete}</p>
                ) : null}

                <div className="mt-6 rounded-2xl border border-border/70 bg-foreground/[0.02] p-4">
                  <p className="text-sm font-medium text-foreground">{t.verifyHeading}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t.verifyHint(billing.billing_email || "—")}
                  </p>
                  <div className="mt-3 flex flex-wrap items-end gap-3">
                    <div>
                      <label htmlFor="sig-code" className="block text-xs text-muted-foreground">
                        {t.codeLabel}
                      </label>
                      <input
                        id="sig-code"
                        value={code}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        disabled={readOnly}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder={t.codePlaceholder}
                        className="mt-1.5 w-36 rounded-xl border border-border bg-background px-4 py-2.5 text-base tabular-nums tracking-[0.3em] text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      />
                    </div>
                    <button
                      type="button"
                      disabled={readOnly || codeMutation.isPending || !billing.billing_email}
                      onClick={() => codeMutation.mutate()}
                      className="rounded-full border border-border px-4 py-2.5 text-xs text-foreground transition-colors hover:bg-foreground/5 disabled:opacity-40"
                    >
                      {codeMutation.isPending
                        ? t.sendingCode
                        : codeSentTo
                          ? t.resendCode
                          : t.sendCode}
                    </button>
                  </div>
                  {codeSentTo ? (
                    <p aria-live="polite" className="mt-3 text-xs text-muted-foreground">
                      {t.codeSent(codeSentTo)}
                    </p>
                  ) : null}
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-4">
                  <button
                    type="button"
                    disabled={
                      readOnly ||
                      signMutation.isPending ||
                      !signerName.trim() ||
                      !agreed ||
                      billingMissing ||
                      code.length !== 6
                    }
                    onClick={() => signMutation.mutate()}
                    className="rounded-full bg-foreground px-6 py-3 text-sm font-medium text-background transition-[opacity,scale] hover:opacity-90 active:scale-[0.96] disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {signMutation.isPending ? t.signing : t.signCta(money(taxTotals.grossCents))}
                  </button>

                  <button
                    type="button"
                    disabled={readOnly}
                    onClick={() => declineMutation.mutate()}
                    className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground disabled:opacity-40"
                  >
                    {t.declineLink}
                  </button>
                </div>
              </div>
            )}

            {error ? (
              <p role="alert" className="mt-4 text-sm text-destructive">
                {error}
              </p>
            ) : null}

            {paid ? (
              <div className="mt-6 rounded-2xl border border-foreground/30 bg-card p-6">
                <p className="font-display text-2xl tracking-tight text-foreground">
                  {t.paidTitle}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t.paidBody}
                </p>
                {billing.billing_email ? (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t.paidEmailed(billing.billing_email)}
                  </p>
                ) : null}
                <a
                  href={`/p/${token}/contract`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-flex items-center justify-center rounded-full border border-border px-5 py-2.5 text-sm text-foreground transition-colors hover:bg-foreground/5"
                >
                  {t.downloadPdf}
                </a>
                <a
                  href={`/p/${token}/certificate`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 ml-3 inline-flex items-center justify-center rounded-full border border-border px-5 py-2.5 text-sm text-foreground transition-colors hover:bg-foreground/5"
                >
                  {t.certificateLink}
                </a>
              </div>
            ) : signed ? (
              <div className="mt-6">
                {clientSecret ? (
                  <StripeEmbeddedCheckoutForm clientSecret={clientSecret} />
                ) : (
                  <button
                    type="button"
                    onClick={() => payMutation.mutate()}
                    disabled={readOnly || payMutation.isPending}
                    className="rounded-full bg-foreground px-6 py-3 text-sm font-medium text-background transition-[opacity,scale] hover:opacity-90 active:scale-[0.96] disabled:opacity-40"
                  >
                    {payMutation.isPending
                      ? t.openingPayment
                      : t.payCta(money(proposal.signature!.total_cents))}
                  </button>
                )}
              </div>
            ) : null}
          </section>
          </Reveal>
        </div>


        {/* Right: decision rail */}
        <aside className={`hidden lg:block lg:sticky lg:top-24 ${railClass("translate-x-4")}`}>
          <div className="rounded-2xl border border-foreground/25 bg-card p-6">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              {t.total}
            </p>
            <p
              aria-live="polite"
              className="mt-2 font-display text-4xl tabular-nums tracking-tight text-foreground"
            >
              {money(total)}
            </p>
            {taxTotals.vatCents > 0 ? (
              <p className="mt-1 text-xs text-muted-foreground">
                {money(taxTotals.grossCents)} {t.grossTotal}
              </p>
            ) : null}

            <p className="mt-2 text-xs text-muted-foreground">
              {money(baseTotal)} {t.base}
              {addons.length > 0 ? ` · ${t.addonCount(selectedAddonCount, addons.length)}` : ""} ·{" "}
              {cycleLabelIn(proposal.billing_cycle, locale)}
            </p>

            {spanDays > 0 ? (
              <button
                type="button"
                onClick={() => scrollToId("timeline")}
                className="mt-5 flex w-full items-baseline justify-between gap-3 rounded-xl border border-border px-4 py-3 text-left transition-colors hover:border-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {t.sections.timeline}
                </span>
                <span className="tabular-nums text-sm text-foreground">
                  {t.timelineDays(spanDays)}
                </span>
              </button>
            ) : null}

            <div className="mt-5">
              {paid ? (
                <p className="rounded-full bg-foreground/10 px-5 py-3 text-center text-sm text-foreground">
                  {t.paidBadge}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => scrollToId("sign")}
                  disabled={readOnly}
                  className="w-full rounded-full bg-foreground px-5 py-3 text-sm font-medium text-background transition-[opacity,scale] hover:opacity-90 active:scale-[0.96] disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {signed ? t.completePayment : t.signAndPay}
                </button>
              )}
            </div>

            <a
              href={`/p/${token}/contract`}
              target="_blank"
              rel="noreferrer"
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-border bg-transparent px-5 py-3 text-sm font-medium text-foreground transition-[opacity,scale] hover:bg-foreground/5 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t.downloadPdf}
            </a>

            {!signed && !paid ? (
              <div className="mt-4 text-center text-xs">
                <button
                  type="button"
                  disabled={readOnly}
                  onClick={() => declineMutation.mutate()}
                  className="text-muted-foreground underline underline-offset-4 hover:text-foreground disabled:opacity-40"
                >
                  {t.decline}
                </button>
              </div>
            ) : null}

            {proposal.valid_until ? (
              <p className="mt-5 border-t border-border pt-4 text-[11px] text-muted-foreground">
                {t.validUntil} {formatDateIn(proposal.valid_until, locale)}
              </p>
            ) : null}
          </div>
        </aside>
      </div>

      {/* Mobile bottom bar */}
      {!paid ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 px-5 py-3 backdrop-blur lg:hidden">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                {t.total} · {cycleLabelIn(proposal.billing_cycle, locale)}
              </p>
              <p
                aria-live="polite"
                className="truncate font-display text-xl tabular-nums tracking-tight text-foreground"
              >
                {money(total)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => scrollToId("sign")}
              disabled={readOnly}
              className="shrink-0 rounded-full bg-foreground px-5 py-3 text-sm font-medium text-background transition-[opacity,scale] active:scale-[0.96] disabled:opacity-40"
            >
              {signed ? t.pay : t.signAndPay}
            </button>
          </div>
        </div>
      ) : null}
    </Shell>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  hint,
  placeholder,
  autoComplete,
  type = "text",
  className = "",
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  placeholder?: string;
  autoComplete?: string;
  type?: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <label htmlFor={id} className="block text-sm text-foreground">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-describedby={hint ? `${id}-hint` : undefined}
        className="mt-2 w-full rounded-xl border border-border bg-background px-4 py-3 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      {hint ? (
        <p id={`${id}-hint`} className="mt-1 text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function Prose({ heading, body, id }: { heading: string; body: string; id?: string }) {

  return (
    <Reveal>
      <section id={id} className="mt-16 scroll-mt-28">
        <SectionTitle>{heading}</SectionTitle>
        <RichText
          text={body}
          className="mt-5 max-w-[68ch] text-base leading-relaxed text-foreground/85"
        />
      </section>
    </Reveal>
  );
}

